import React, { useCallback, useState } from 'react';
import { Button, ButtonProps } from 'src/components/Button';
import { Dialog } from 'src/components/Dialog';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { NonEmptyArray } from 'src/types/utilityTypes';

interface ButtonPropsWithLabel extends ButtonProps {
  label: string;
}

interface DialogProps {
  open: boolean;
  title?: string;
  actions: React.ReactNode | NonEmptyArray<ButtonPropsWithLabel>;
  noWrap?: boolean;
  children: React.ReactNode;
}

export const ConfirmDialog: React.FC<DialogProps> = ({
  open,
  title,
  actions,
  noWrap = false,
  children,
}) => {
  return (
    <Dialog
      open={open}
      title={title}
      actions={
        <>
          {Array.isArray(actions) && (
            <Stack sx={{ width: '100%' }} spacing={1}>
              {actions.map((buttonProps, i) => (
                <Button key={i} fullWidth {...buttonProps}>
                  {buttonProps.children || buttonProps.label}
                </Button>
              ))}
            </Stack>
          )}
          {!Array.isArray(actions) && actions}
        </>
      }
      noWrap={noWrap}
      titleAlignment="center"
    >
      <Typography
        variant="body1"
        sx={{
          textAlign: 'center',
        }}
      >
        {children}
      </Typography>
    </Dialog>
  );
};

interface UseConfirmDialogOptions {
  title: string;
  message?: string | (() => string) | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: 'primary' | 'secondary' | 'error' | 'warning';
}

export const useConfirmDialogComponent = (
  initialOptions?: Partial<UseConfirmDialogOptions>
): UseConfirmDialogReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<UseConfirmDialogOptions>({
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    ...initialOptions,
  });
  const [resolvePromise, setResolvePromise] = useState<
    ((value: boolean) => void) | null
  >(null);

  const confirm = useCallback(
    (
      updateOptions?: Partial<UseConfirmDialogOptions> | string
    ): Promise<boolean> => {
      return new Promise((resolve) => {
        setOptions((prevOptions) => ({
          ...prevOptions,
          ...(typeof updateOptions === 'string'
            ? { message: updateOptions }
            : updateOptions),
        }));
        setIsOpen(true);
        setResolvePromise(() => resolve);
      });
    },
    []
  );

  const handleConfirm = useCallback(() => {
    setIsOpen(false);
    if (resolvePromise) resolvePromise(true);
  }, [resolvePromise]);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    if (resolvePromise) resolvePromise(false);
  }, [resolvePromise]);

  const dialogProps: DialogProps = {
    open: isOpen,
    title: options.title,
    children:
      typeof options.message === 'function'
        ? options.message()
        : options.message,
    actions: [
      {
        label: options.cancelText || 'Cancel',
        onClick: handleCancel,
        color: 'secondary',
      },
      {
        label: options.confirmText || 'Confirm',
        onClick: handleConfirm,
        color: options.confirmColor || 'error',
        variant: 'contained',
      },
    ],
    noWrap: false,
  };

  return [dialogProps, confirm];
};

export type UseConfirmDialogReturn = [
  DialogProps,
  (
    updateOptions?: Partial<UseConfirmDialogOptions> | string
  ) => Promise<boolean>
];
